import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { getDocumentById, getRelatedDocuments } from '../data/documents/index.js';
import Table from './Table';
import '../styles/DocumentDetailPage.css';

const CopyPromptBlock = ({ content }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const textarea = window.document.createElement('textarea');
      textarea.value = content;
      window.document.body.appendChild(textarea);
      textarea.select();
      window.document.execCommand('copy');
      window.document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="docs-copy-prompt-block">
      <div className="docs-copy-prompt-header">
        <span className="docs-copy-prompt-label">Prompt listo para copiar</span>
        <button
          className={`docs-copy-prompt-btn ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
        >
          {copied ? '✅ Copiado' : '📋 Copiar todo'}
        </button>
      </div>
      <pre className="docs-copy-prompt-content">{content}</pre>
    </div>
  );
};

const DocumentDetailPage = () => {
  const { docId } = useParams();
  const [document, setDocument] = useState(null);
  const [relatedDocs, setRelatedDocs] = useState([]);
  const [activeSection, setActiveSection] = useState(0);
  const [feedback, setFeedback] = useState(null); // null, 'helpful', 'not-helpful'
  const sectionRefs = useRef([]);

  useEffect(() => {
    // Scroll hacia arriba al cargar un nuevo documento
    window.scrollTo(0, 0);
    
    const foundDoc = getDocumentById(docId);
    if (foundDoc) {
      setDocument(foundDoc);
      setActiveSection(0);
      setFeedback(null); // Resetear feedback al cambiar documento
      
      // Calcular total de secciones (explanations + steps)
      const totalSections = (foundDoc.explanations?.length || 0) + (foundDoc.steps?.length || 0);
      sectionRefs.current = sectionRefs.current.slice(0, totalSections);
      
      // Obtener documentos relacionados
      const related = getRelatedDocuments(docId, 3);
      setRelatedDocs(related);
    } else {
      setDocument(null);
    }
  }, [docId]);

  // Renderizar tablas y configurar botones de código después de que el contenido se haya montado
  useEffect(() => {
    if (document) {
      const renderTables = () => {
        const tablePlaceholders = window.document.querySelectorAll('.docs-table-placeholder');
        tablePlaceholders.forEach(placeholder => {
          const tableId = placeholder.getAttribute('data-table-id');
          if (window.documentTables && window.documentTables[tableId] && !placeholder.dataset.rendered) {
            const tableData = window.documentTables[tableId];
            
            // Marcar como renderizado para evitar duplicados
            placeholder.dataset.rendered = 'true';
            
            // Crear contenedor para React
            const tableContainer = window.document.createElement('div');
            placeholder.parentNode.replaceChild(tableContainer, placeholder);
            
            // Importar ReactDOM dinámicamente y renderizar
            import('react-dom/client').then(({ createRoot }) => {
              const root = createRoot(tableContainer);
              root.render(
                React.createElement(Table, {
                  headers: tableData.headers,
                  rows: tableData.rows,
                  title: tableData.title,
                  className: tableData.className
                })
              );
            }).catch(() => {
              // Fallback para versiones anteriores de React
              import('react-dom').then(({ render }) => {
                render(
                  React.createElement(Table, {
                    headers: tableData.headers,
                    rows: tableData.rows,
                    title: tableData.title,
                    className: tableData.className
                  }),
                  tableContainer
                );
              });
            });
          }
        });
      };

      const fixDoubleEscaping = () => {
        // Corregir cualquier problema de doble escape en bloques de código
        const codeElements = window.document.querySelectorAll('.docs-code-block code');
        codeElements.forEach(codeEl => {
          if (codeEl.dataset.fixed) return;
          
          const originalHtml = codeEl.innerHTML;
          // Verificar si hay doble escape y corregirlo
          const correctedHtml = originalHtml
            .replace(/&amp;lt;/g, '&lt;')
            .replace(/&amp;gt;/g, '&gt;')
            .replace(/&amp;amp;/g, '&amp;')
            .replace(/&amp;quot;/g, '&quot;')
            .replace(/&amp;#39;/g, '&#39;');
          
          if (originalHtml !== correctedHtml) {
            codeEl.innerHTML = correctedHtml;
          }
          
          codeEl.dataset.fixed = 'true';
        });
      };

      const addCopyButtons = () => {
        const codeBlocks = window.document.querySelectorAll('.docs-code-block');
        codeBlocks.forEach(block => {
          if (block.dataset.copyButtonAdded) return;
          
          const codeElement = block.querySelector('code');
          if (codeElement) {
            const copyButton = window.document.createElement('button');
            copyButton.className = 'docs-copy-code-btn';
            copyButton.innerHTML = '📋 Copiar';
            copyButton.title = 'Copiar código al portapapeles';
            
            copyButton.addEventListener('click', () => {
              // Obtener el texto del código y decodificar entidades HTML
              let codeText = codeElement.textContent || codeElement.innerText;
              
              // Decodificar entidades HTML comunes para el texto copiado
              codeText = codeText
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'");
              
              navigator.clipboard.writeText(codeText).then(() => {
                copyButton.innerHTML = '✅ Copiado';
                copyButton.classList.add('copied');
                setTimeout(() => {
                  copyButton.innerHTML = '📋 Copiar';
                  copyButton.classList.remove('copied');
                }, 2000);
              }).catch(() => {
                // Fallback para navegadores que no soportan clipboard API
                const textArea = window.document.createElement('textarea');
                textArea.value = codeText;
                window.document.body.appendChild(textArea);
                textArea.select();
                window.document.execCommand('copy');
                window.document.body.removeChild(textArea);
                
                copyButton.innerHTML = '✅ Copiado';
                copyButton.classList.add('copied');
                setTimeout(() => {
                  copyButton.innerHTML = '📋 Copiar';
                  copyButton.classList.remove('copied');
                }, 2000);
              });
            });
            
            block.style.position = 'relative';
            block.appendChild(copyButton);
            block.dataset.copyButtonAdded = 'true';
          }
        });
      };

      // Delay para asegurar que el DOM esté actualizado
      setTimeout(() => {
        renderTables();
        fixDoubleEscaping();
        addCopyButtons();
      }, 200);
    }
  }, [document]);

  const formatContent = (content) => {
    return content
      // Process tables first (before other formatting)
      .replace(/\[TABLE:(.*?)\]\n([\s\S]*?)\[\/TABLE\]/g, (match, title, tableContent) => {
        try {
          const lines = tableContent.trim().split('\n');
          if (lines.length < 2) return match;
          
          // Parse headers (first line)
          const headers = lines[0].split('|').map(h => h.trim()).filter(h => h);
          
          // Parse rows (remaining lines)
          const rows = lines.slice(1).map(line => 
            line.split('|').map(cell => cell.trim()).filter(cell => cell)
          ).filter(row => row.length > 0);
          
          // Generate unique ID for this table
          const tableId = `table_${Math.random().toString(36).substr(2, 9)}`;
          
          // Store table data in a global variable for React to pick up
          if (!window.documentTables) window.documentTables = {};
          window.documentTables[tableId] = {
            headers,
            rows,
            title: title || '',
            className: title.toLowerCase().includes('css') ? 'css-reference' : ''
          };
          
          return `<div id="${tableId}" class="docs-table-placeholder" data-table-id="${tableId}"></div>`;
        } catch (error) {
          console.error('Error parsing table:', error);
          return match;
        }
      })
      // Bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic text
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Code blocks de múltiples líneas estándar (con lenguaje opcional)
      .replace(/```(\w+)?\n?([\s\S]*?)```/g, (match, language, code) => {
        const lang = language || '';
        // Crear un ID único para este bloque de código
        const codeId = `code-${Math.random().toString(36).substr(2, 9)}`;
        
        // Limpiar el código y escapar para HTML de manera más cuidadosa
        const cleanCode = code.trim();
        const escapedCode = cleanCode
          .replace(/&amp;/g, '&') // Primero decodificar cualquier entidad ya escapada
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&/g, '&amp;') // Luego volver a escapar correctamente
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
        
        return `<div class="docs-code-block" id="${codeId}">
          ${lang ? `<div class="docs-code-header">${lang}</div>` : ''}
          <pre><code>${escapedCode}</code></pre>
        </div>`;
      })
      // Code blocks con formato específico de los datos (backticks simples con \n)
      .replace(/`(\w+)\n([\s\S]*?)\n`/g, (match, language, code) => {
        const codeId = `code-${Math.random().toString(36).substr(2, 9)}`;
        
        const cleanCode = code
          .replace(/\\n/g, '\n') // Convertir \n literales a saltos de línea reales
          .replace(/&amp;/g, '&') // Decodificar entidades ya escapadas
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'");
        
        const escapedCode = cleanCode
          .replace(/&/g, '&amp;') // Volver a escapar correctamente
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
        
        return `<div class="docs-code-block" id="${codeId}">
          <div class="docs-code-header">${language}</div>
          <pre><code>${escapedCode}</code></pre>
        </div>`;
      })
      // Code blocks (inline) - para código corto que no es multilinea
      .replace(/`([^`\n]+)`/g, '<code class="docs-inline-code">$1</code>')
      // Procesar listas con viñetas (formato \n- específico del contenido)
      .replace(/([^:]*:)(\n-\s[^\n]+)+/g, (match, intro, list) => {
        const items = list
          .split(/\n-\s/)
          .filter(item => item.trim().length > 0);
        
        if (items.length === 0) return match;
        
        const listItems = items.map(item => `<li>${item.trim()}</li>`).join('');
        return `${intro.trim()}<ul>${listItems}</ul>`;
      })
      // Procesar listas con viñetas sin texto introductorio
      .replace(/(\n-\s[^\n]+)+/g, (match) => {
        const items = match
          .split(/\n-\s/)
          .filter(item => item.trim().length > 0);
        
        if (items.length === 0) return match;
        
        const listItems = items.map(item => `<li>${item.trim()}</li>`).join('');
        return `<ul>${listItems}</ul>`;
      })
      // Procesar listas numeradas
      .replace(/(\n\d+\.\s[^\n]+)+/g, (match) => {
        const items = match
          .split(/\n\d+\.\s/)
          .filter(item => item.trim().length > 0);
        
        if (items.length === 0) return match;
        
        const listItems = items.map(item => `<li>${item.trim()}</li>`).join('');
        return `<ol>${listItems}</ol>`;
      })
      // Links
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      // Line breaks
      .replace(/\n/g, '<br>');
  };

  const scrollToSection = (sectionIndex) => {
    setActiveSection(sectionIndex);
    const element = sectionRefs.current[sectionIndex];
    if (element) {
      const headerOffset = 100; // Altura aproximada del header + padding extra
      const elementPosition = element.offsetTop - headerOffset;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'básico': return '#10b981';
      case 'intermedio': return '#f59e0b';
      case 'avanzado': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Manejar feedback de usuarios
  const handleFeedback = (type) => {
    setFeedback(type);
    
    // Aquí puedes añadir lógica para enviar el feedback a tu backend
    console.log(`Usuario marcó el documento como: ${type}`);
    
    // Simular envío al servidor (opcional)
    // sendFeedbackToServer(docId, type);
  };

  // Crear array combinado de secciones para navegación
  const getAllSections = () => {
    const sections = [];
    
    if (document.explanations) {
      document.explanations.forEach((explanation, index) => {
        sections.push({
          type: 'explanation',
          index,
          title: explanation.title,
          content: explanation
        });
      });
    }
    
    if (document.steps) {
      document.steps.forEach((step, index) => {
        sections.push({
          type: 'step',
          index,
          title: step.title,
          content: step
        });
      });
    }
    
    return sections;
  };

  if (!document) {
    return (
      <div className="docs-detail-page">
        <div className="container">
          <div className="docs-not-found">
            <h1>Documento no encontrado</h1>
            <p>El documento que buscas no existe o ha sido movido.</p>
            <Link to="/docs" className="docs-btn docs-btn-outline">
              Volver a Documentación
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const allSections = getAllSections();

  return (
    <div className="docs-detail-page">
      {/* Document Header */}
      <section className="docs-detail-header">
        <div className="container">
          <motion.div 
            className="docs-detail-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="docs-breadcrumb">
              <Link to="/docs">Documentación</Link>
              <span className="docs-breadcrumb-separator">→</span>
              <Link 
                to={`/docs?category=${encodeURIComponent(document.category)}`}
                className="docs-breadcrumb-category"
              >
                {document.category}
              </Link>
              <span className="docs-breadcrumb-separator">→</span>
              <span className="docs-breadcrumb-current">{document.title}</span>
            </div>
            
            <div className="docs-meta">
              <span className="docs-category">{document.category}</span>
              {document.difficulty && (
                <span 
                  className="docs-difficulty"
                  style={{ backgroundColor: getDifficultyColor(document.difficulty) }}
                >
                  {document.difficulty}
                </span>
              )}
              {document.tags && (
                <div className="docs-tags">
                  {document.tags.map(tag => (
                    <span key={tag} className="docs-tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            
            <h1 className="docs-title">{document.title}</h1>
            <p className="docs-description">{document.description}</p>
            
            {document.lastUpdated && (
              <div className="docs-updated">
                Última actualización: {new Date(document.lastUpdated).toLocaleDateString('es-ES')}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Document Content */}
      <section className="docs-content-section">
        <div className="container">
          <div className="docs-layout">
            {/* Navigation Sidebar */}
            <motion.aside 
              className="docs-sidebar"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="docs-sidebar-sticky">
                <h3 className="docs-sidebar-title">Contenido del Documento</h3>
                {allSections.length > 0 && (
                  <nav className="docs-sections-nav">
                    {allSections.map((section, index) => (
                      <button
                        key={`${section.type}-${section.index}`}
                        className={`docs-section-nav-item ${activeSection === index ? 'active' : ''} ${section.type}`}
                        onClick={() => scrollToSection(index)}
                      >
                        <span className="docs-section-type">
                          {section.type === 'explanation' ? '📖' : '⚡'}
                        </span>
                        <span className="docs-section-title">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                )}
                
                <div className="docs-sidebar-actions">
                  <Link to="/docs" className="docs-btn docs-btn-outline">
                    ← Volver a Docs
                  </Link>
                </div>
              </div>
            </motion.aside>

            {/* Main Content */}
            <motion.main 
              className="docs-main"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {/* Main Image */}
              {document.mainImage && (
                <div className="docs-main-image">
                  <img src={document.mainImage} alt={document.title} />
                </div>
              )}

              {/* Main Video */}
              {document.mainVideo && (
                <div className="docs-main-video">
                  <div className="docs-video-container">
                    <iframe
                      src={document.mainVideo.replace('/view?usp=drive_link', '/preview')}
                      title={`Video tutorial: ${document.title}`}
                      frameBorder="0"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  </div>
                  <div className="docs-video-caption">
                    📹 Video tutorial: {document.title}
                  </div>
                </div>
              )}
              
              {/* Explanations Section */}
              {document.explanations && document.explanations.length > 0 && (
                <div className="docs-explanations">
                  <div className="docs-section-header">
                    <h2 className="docs-section-header-title">📖 Explicaciones y Conceptos</h2>
                    <p className="docs-section-header-desc">Información teórica y conceptual fundamental</p>
                  </div>
                  
                  {document.explanations.map((explanation, index) => {
                    const globalIndex = index;
                    return (
                      <motion.div
                        key={`explanation-${index}`}
                        ref={el => sectionRefs.current[globalIndex] = el}
                        className={`docs-explanation-item ${activeSection === globalIndex ? 'active' : ''}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <div className="docs-explanation-header">
                          <div className="docs-explanation-icon">📖</div>
                          <h3 className="docs-explanation-title">{explanation.title}</h3>
                        </div>
                        
                        <div className="docs-explanation-content">
                          <div 
                            className="docs-explanation-text"
                            dangerouslySetInnerHTML={{ 
                              __html: formatContent(explanation.content)
                            }}
                          />

                          {/* Copy Block */}
                          {explanation.copyBlock && (
                            <CopyPromptBlock content={explanation.copyBlock} />
                          )}
                          
                          {/* Explanation Video */}
                          {explanation.video && (
                            <div className="docs-explanation-video">
                              <div className="docs-video-container">
                                <iframe
                                  src={(() => {
                                    // Extraer el ID del archivo de Google Drive
                                    const match = explanation.video.match(/\/d\/([a-zA-Z0-9-_]+)/);
                                    const fileId = match ? match[1] : '';
                                    return `https://drive.google.com/file/d/${fileId}/preview?rm=minimal`;
                                  })()}
                                  title={`Video tutorial: ${explanation.title}`}
                                  frameBorder="0"
                                  allowFullScreen
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                />
                              </div>
                            </div>
                          )}

                          {/* Explanation Images */}
                          {explanation.images && explanation.images.length > 0 && (
                            <div className="docs-images">
                              {explanation.images.map((image, imgIndex) => (
                                <div key={imgIndex} className="docs-image">
                                  <img 
                                    src={image} 
                                    alt={`${explanation.title} - Imagen ${imgIndex + 1}`}
                                    loading="lazy"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Video Tutorial Section */}
              {document.videoTutorial && (
                <div className="docs-video-tutorial">
                  <div className="docs-section-header">
                    <h2 className="docs-section-header-title">🎥 {document.videoTutorial.title}</h2>
                    {document.videoTutorial.description && (
                      <p className="docs-section-header-desc">{document.videoTutorial.description}</p>
                    )}
                  </div>
                  
                  <motion.div
                    className="docs-tutorial-video"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="docs-video-container">
                      <iframe
                        src={(() => {
                          // Convertir URL de YouTube a formato embed sin sugerencias
                          let url = document.videoTutorial.url;
                          if (url.includes('youtu.be/')) {
                            const videoId = url.split('youtu.be/')[1].split('?')[0];
                            return `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&modestbranding=1`;
                          } else if (url.includes('youtube.com/watch')) {
                            const videoId = url.split('v=')[1].split('&')[0];
                            return `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&modestbranding=1`;
                          } else if (url.includes('/d/')) {
                            // Mantener compatibilidad con Google Drive
                            const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
                            const fileId = match ? match[1] : '';
                            return `https://drive.google.com/file/d/${fileId}/preview?rm=minimal`;
                          }
                          return url;
                        })()}
                        title={document.videoTutorial.title}
                        frameBorder="0"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    </div>
                  </motion.div>
                </div>
              )}
              
              {/* Steps Section */}
              {document.steps && document.steps.length > 0 && (
                <div className="docs-steps">
                  <div className="docs-section-header">
                    <h2 className="docs-section-header-title">⚡ Pasos Prácticos</h2>
                    <p className="docs-section-header-desc">Instrucciones paso a paso para implementar</p>
                  </div>
                  
                  {document.steps.map((step, index) => {
                    const globalIndex = (document.explanations?.length || 0) + index;
                    return (
                      <motion.div
                        key={`step-${index}`}
                        ref={el => sectionRefs.current[globalIndex] = el}
                        className={`docs-step-item ${activeSection === globalIndex ? 'active' : ''}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <div className="docs-step-header">
                          <div className="docs-step-badge">
                            Paso {index + 1}
                          </div>
                          <h3 className="docs-step-title">{step.title}</h3>
                        </div>
                        
                        <div className="docs-step-content">
                          <div 
                            className="docs-step-text"
                            dangerouslySetInnerHTML={{ 
                              __html: formatContent(step.content)
                            }}
                          />
                          
                          {/* Step Video */}
                          {step.video && (
                            <div className="docs-explanation-video">
                              <div className="docs-video-container">
                                <iframe
                                  src={(() => {
                                    // Extraer el ID del archivo de Google Drive
                                    const match = step.video.match(/\/d\/([a-zA-Z0-9-_]+)/);
                                    const fileId = match ? match[1] : '';
                                    return `https://drive.google.com/file/d/${fileId}/preview?rm=minimal`;
                                  })()}
                                  title={`Video tutorial: ${step.title}`}
                                  frameBorder="0"
                                  allowFullScreen
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                />
                              </div>
                            </div>
                          )}

                          {/* Step Images */}
                          {step.images && step.images.length > 0 && (
                            <div className="docs-images">
                              {step.images.map((image, imgIndex) => (
                                <div key={imgIndex} className="docs-image">
                                  <img 
                                    src={image} 
                                    alt={`${step.title} - Imagen ${imgIndex + 1}`}
                                    loading="lazy"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Table Section */}
              {document.table && (
                <div className="docs-table-section">
                  <div className="docs-section-header">
                    <h2 className="docs-section-header-title">📊 {document.table.title || 'Tabla de Referencia'}</h2>
                    <p className="docs-section-header-desc">Información estructurada para consulta rápida</p>
                  </div>
                  
                  <motion.div
                    className="docs-table-wrapper"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Table 
                      headers={document.table.headers}
                      rows={document.table.rows}
                      title={document.table.title}
                      className="docs-reference-table"
                    />
                  </motion.div>
                </div>
              )}

              {/* Related Documents */}
              {relatedDocs.length > 0 && (
                <section className="docs-related">
                  <h3 className="docs-related-title">Documentos Relacionados</h3>
                  <div className="docs-related-grid">
                    {relatedDocs.map(relatedDoc => (
                      <Link 
                        key={relatedDoc.id}
                        to={`/docs/${relatedDoc.id}`}
                        className="docs-related-card"
                      >
                        <div className="docs-related-content">
                          <h4>{relatedDoc.title}</h4>
                          <p>{relatedDoc.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Document Footer */}
              <div className="docs-footer">
                <div className="docs-feedback">
                  {feedback === null ? (
                    <>
                      <h4>¿Te fue útil esta documentación?</h4>
                      <div className="docs-feedback-buttons">
                        <button 
                          className="docs-feedback-btn docs-feedback-yes"
                          onClick={() => handleFeedback('helpful')}
                        >
                          👍 Sí, me ayudó
                        </button>
                        <button 
                          className="docs-feedback-btn docs-feedback-no"
                          onClick={() => handleFeedback('not-helpful')}
                        >
                          👎 No me ayudó
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="docs-feedback-response">
                      {feedback === 'helpful' ? (
                        <>
                          <h4>¡Gracias por tu feedback! 👍</h4>
                          <p>Nos alegra saber que esta documentación te fue útil.</p>
                        </>
                      ) : (
                        <>
                          <h4>Gracias por tu feedback 💭</h4>
                          <p>Trabajaremos para mejorar esta documentación. Tu opinión es muy valiosa.</p>
                        </>
                      )}
                      <button 
                        className="docs-feedback-btn docs-feedback-reset"
                        onClick={() => setFeedback(null)}
                      >
                        Cambiar respuesta
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="docs-navigation">
                  <Link to="/docs" className="docs-nav-link">
                    ← Todos los documentos
                  </Link>
                </div>
              </div>
            </motion.main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocumentDetailPage; 